import { useEffect, useState } from 'react';
import { ArrowRight, Heart, Brain, Smile, Wallet, Briefcase, Compass, Sparkles, Flower2 } from 'lucide-react';
import { useTranslation } from '../i18n/LanguageContext';
import LanguageSwitcher from './LanguageSwitcher';

// Dimension copy + visuals (premium, calm, on-brand)
// `tk` is the i18n key for the dimension label/explanation; `dim` is the canonical
// English name used by the backend.
const DIMENSIONS = {
  Physical: { icon: Heart, accent: '#38BDF8', tk: 'physical' },
  Mental: { icon: Brain, accent: '#818CF8', tk: 'mental' },
  Emotional: { icon: Smile, accent: '#C084FC', tk: 'emotional' },
  Financial: { icon: Wallet, accent: '#34D399', tk: 'financial' },
  Career: { icon: Briefcase, accent: '#FBBF24', tk: 'career' },
  Lifestyle: { icon: Compass, accent: '#F472B6', tk: 'lifestyle' },
  Spiritual: { icon: Flower2, accent: '#A78BFA', tk: 'spiritual' },
  'Self-Care': { icon: Sparkles, accent: '#F59E0B', tk: 'self_care' },
};

function DimensionIntro({ dimension, sectionIndex, totalSections, onContinue }) {
  const { t } = useTranslation();
  const [showButton, setShowButton] = useState(false);
  const meta = DIMENSIONS[dimension] || DIMENSIONS.Physical;
  const Icon = meta.icon;
  const displayName = t(`dim.${meta.tk}`);
  const explanationText = t(`dim.${meta.tk}.text`);

  useEffect(() => {
    const timer = setTimeout(() => setShowButton(true), 900);
    return () => clearTimeout(timer);
  }, [dimension]);

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center px-6"
      style={{ backgroundColor: '#F8FAFC' }}
      data-testid="dimension-intro"
    >
      {/* Soft accent gradient overlay (uses dimension accent) */}
      <div
        className="absolute inset-0"
        style={{
          background: `radial-gradient(ellipse at center, ${meta.accent}1F 0%, rgba(248, 250, 252, 0.97) 70%)`,
        }}
      />
      <div
        className="absolute top-1/4 left-1/4 w-96 h-96 rounded-full blur-3xl opacity-25 dim-orb-1"
        style={{ background: `radial-gradient(circle, ${meta.accent}, transparent)` }}
      />
      <div
        className="absolute bottom-1/4 right-1/4 w-96 h-96 rounded-full blur-3xl opacity-25 dim-orb-2"
        style={{ background: `radial-gradient(circle, ${meta.accent}, transparent)` }}
      />

      <div className="relative z-10 max-w-2xl w-full text-center">
        {/* Language switcher inside the overlay (so it's reachable above the fixed inset) */}
        <div className="absolute -top-12 sm:-top-16 right-0 sm:right-2">
          <LanguageSwitcher />
        </div>

        {/* Section label */}
        <div
          className="mb-8"
          style={{ animation: 'pauseFadeIn 0.6s ease-out forwards' }}
        >
          <div
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border shadow-soft"
            style={{ borderColor: `${meta.accent}40` }}
            data-testid="dimension-section-label"
          >
            <Icon className="w-4 h-4" style={{ color: meta.accent }} />
            <span className="text-xs font-semibold tracking-[0.2em] uppercase" style={{ color: meta.accent }}>
              {t('assess.section', { n: sectionIndex, total: totalSections })} · {displayName}
            </span>
          </div>
        </div>

        {/* Dimension explanation */}
        <div
          className="mb-12"
          style={{
            animation: 'pauseFadeIn 0.9s ease-out 0.25s forwards',
            opacity: 0,
          }}
        >
          <h2
            className="text-3xl md:text-4xl lg:text-5xl font-light leading-tight text-[#0F172A]"
            style={{ fontFamily: 'Outfit, sans-serif' }}
            data-testid="dimension-text"
          >
            <span className="italic">{explanationText}</span>
          </h2>
        </div>

        {showButton && (
          <button
            onClick={onContinue}
            className="group px-10 py-4 bg-gradient-to-r from-[#4F46E5] to-[#6366F1] hover:from-[#6366F1] hover:to-[#818CF8] text-white font-semibold rounded-2xl glow-primary inline-flex items-center gap-3"
            style={{
              animation: 'pauseFadeIn 0.5s ease-out forwards',
              opacity: 0,
            }}
            data-testid="dimension-continue-btn"
          >
            {t('assess.continue')}
            <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
          </button>
        )}
      </div>

      <style>{`
        @keyframes pauseFadeIn {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .dim-orb-1 { animation: dimFloat1 8s ease-in-out infinite; }
        .dim-orb-2 { animation: dimFloat2 10s ease-in-out infinite; }
        @keyframes dimFloat1 {
          0%, 100% { transform: translate(0, 0); }
          50% { transform: translate(30px, -30px); }
        }
        @keyframes dimFloat2 {
          0%, 100% { transform: translate(0, 0); }
          50% { transform: translate(-30px, 30px); }
        }
      `}</style>
    </div>
  );
}

export default DimensionIntro;
